import React from "react"
import Reveal from "./Reveal.jsx"

const STEPS = [
  {
    eyebrow: "Install",
    title: "Paste one script tag.",
    body:
      "On every page of your shop. Loaded async, cached at the edge, sub-20kb. That's the whole install — no theme edits, no plugins, no platform lock-in.",
    detail: "<script src=\"https://cdn.shopshift.io/s/{api_key}.js\"></script>",
  },
  {
    eyebrow: "Discover",
    title: "AI learns your shop.",
    body:
      "Headless Chromium renders every page. Vision AI extracts your color palette, typography, and design archetype. Verified facts (shipping policy, free-ship threshold, return window) get pulled verbatim from your own pages.",
    detail: "Crawl · classify · render · extract",
  },
  {
    eyebrow: "Propose",
    title: "AI walks the funnel and writes tests.",
    body:
      "Diagnose where visitors drop off → research the surface → brainstorm options → pick the safest 1–2 → shape concrete DOM changes → screenshot before & after. Every step audited and replayable.",
    detail: "Diagnose → Research → Brainstorm → Pick → Shape",
  },
  {
    eyebrow: "Decide",
    title: "Winners apply, losers retire.",
    body:
      "Bayesian posteriors update nightly. The moment a challenger crosses 95% probability of beating control, it becomes the winner — automatically. You get an email. Nothing else changes.",
    detail: "P(challenger > control) ≥ 0.95",
  },
]

export default function HowItWorks() {
  return (
    <section
      id="how-it-works"
      className="border-t border-[color:var(--color-rule)] bg-[color:var(--color-surface-card)]"
    >
      <div className="max-w-6xl mx-auto px-6 py-32">
        <Reveal>
          <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[color:var(--color-ai)] mb-4">
            § 04 · How it works
          </p>
          <h2 className="font-sans text-[40px] tracking-[-0.035em] font-medium leading-[1.1] max-w-2xl">
            Paste, walk away. Come back to better numbers.
          </h2>
        </Reveal>

        <div className="mt-20 relative">
          {/* vertical connector — sits in the left gutter, behind step content */}
          <div
            aria-hidden
            className="absolute left-[42px] top-12 bottom-12 w-px bg-[color:var(--color-rule)] hidden md:block"
          />

          <ol className="flex flex-col gap-24">
            {STEPS.map((step, idx) => (
              <li key={step.eyebrow}>
                <Reveal>
                  <div className="grid grid-cols-1 md:grid-cols-[88px_1fr] gap-x-10 gap-y-4 items-start">
                    <div
                      className="font-sans font-medium text-[color:var(--color-ai)] leading-none"
                      style={{ fontSize: "72px", letterSpacing: "-0.045em", fontVariantNumeric: "tabular-nums" }}
                    >
                      {String(idx + 1).padStart(2, "0")}
                    </div>
                    <div className="flex flex-col gap-4 max-w-2xl">
                      <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[color:var(--color-ink-tertiary)]">
                        Step {String(idx + 1).padStart(2, "0")} · {step.eyebrow}
                      </p>
                      <h3 className="font-sans text-[28px] tracking-[-0.03em] font-medium leading-[1.15] text-[color:var(--color-ink-primary)]">
                        {step.title}
                      </h3>
                      <p className="font-sans text-[15px] text-[color:var(--color-ink-secondary)] leading-[1.6]">
                        {step.body}
                      </p>
                      <p className="mt-2 inline-flex items-center font-mono text-[11px] tracking-[0.04em] text-[color:var(--color-ink-faint)] bg-[color:var(--color-surface-subtle)] border border-[color:var(--color-rule)] rounded-[var(--radius-pill)] px-2.5 py-1 self-start">
                        {step.detail}
                      </p>
                    </div>
                  </div>
                </Reveal>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  )
}
